/**
 * <s-hover-cycle> — while a mouse rests on a product card, crossfades through its variant photos
 * one per interval, looping, and returns to the cover image when the pointer leaves the card.
 * Every link in the card to this product follows the photo on screen (?variant=), so clicking
 * opens the product on that same variant. Touch devices keep the still cover image.
 */
class SHoverCycle extends HTMLElement {
  #ac;
  #timer;
  #index = -1;

  connectedCallback() {
    const slides = [...this.querySelectorAll('[data-slide]')];
    // The card shell lays a full-size link over its body, so listen on the whole card, not the image link.
    const card = this.closest('article') || this.closest('a') || this.parentElement;
    if (!slides.length || !card) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const interval = Number(this.dataset.interval) || 1000;
    const productPath = new URL(this.dataset.productUrl || '', window.location.href).pathname;
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

    const productLinks = () => [...card.querySelectorAll('a[href]')].filter((a) => {
      try {
        return new URL(a.href, window.location.href).pathname === productPath;
      } catch {
        return false;
      }
    });

    const setVariant = (variantId) => {
      for (const link of productLinks()) {
        const url = new URL(link.href, window.location.href);
        if (variantId) url.searchParams.set('variant', variantId);
        else url.searchParams.delete('variant');
        link.setAttribute('href', url.pathname + url.search + url.hash);
      }
    };

    const show = (i) => {
      slides.forEach((slide, n) => slide.toggleAttribute('data-active', n === i));
      this.#index = i;
      setVariant(i >= 0 ? slides[i].dataset.variantId : '');
    };

    const start = () => {
      if (!canHover.matches || this.#timer) return;
      slides.forEach((slide) => slide.querySelector('img')?.setAttribute('loading', 'eager'));
      show(0);
      // With one photo, flip between it and the cover (index -1) so the card keeps changing.
      this.#timer = setInterval(() => {
        if (slides.length === 1) show(this.#index === 0 ? -1 : 0);
        else show((this.#index + 1) % slides.length);
      }, interval);
    };

    const stop = () => {
      clearInterval(this.#timer);
      this.#timer = undefined;
      show(-1);
    };

    card.addEventListener('mouseenter', start, opts);
    card.addEventListener('mouseleave', stop, opts);
    // Freeze on click so the page opens on the exact photo and variant that was showing.
    card.addEventListener('click', () => clearInterval(this.#timer), opts);
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
    this.#ac?.abort();
  }
}

if (!customElements.get('s-hover-cycle')) {
  customElements.define('s-hover-cycle', SHoverCycle);
}
